/**
 * IndexedDB persistence for the local replica.
 *
 * The browser owns a complete copy of every project the signed-in user can see, so the app
 * reads nothing from the network at run time. Writes land here first and are replayed to the
 * server from the outbox whenever connectivity allows.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

import { EntityKind, type EntityMap } from '../../shared/types.js';
import type { PushOp, SyncCursor } from '../../shared/sync-protocol.js';

/** Name of the IndexedDB database holding the replica. */
const DATABASE_NAME = 'anwesenheit';

/** Schema version; bump together with an upgrade branch in `openDatabase`. */
const DATABASE_VERSION = 2;

/** Key of the replication cursor in the `meta` store. */
const CURSOR_KEY = 'cursor';

/** Key of the cached identity in the `meta` store. */
const IDENTITY_KEY = 'identity';

/** The account the replica belongs to, cached so the app can start offline. */
export interface CachedIdentity {
  userId: string;
  email: string;
}

/** A queued local write awaiting delivery to the server. */
export interface OutboxEntry {
  /** Auto-incremented delivery order. */
  id?: number;
  op: PushOp;
}

interface Schema extends DBSchema {
  projects: { key: string; value: EntityMap[EntityKind.Project] };
  memberships: { key: string; value: EntityMap[EntityKind.Membership]; indexes: { byProject: string } };
  people: { key: string; value: EntityMap[EntityKind.Person]; indexes: { byProject: string } };
  sessionOverrides: { key: string; value: EntityMap[EntityKind.SessionOverride]; indexes: { byProject: string } };
  attendanceMarks: { key: string; value: EntityMap[EntityKind.AttendanceMark]; indexes: { byProject: string } };
  notes: { key: string; value: EntityMap[EntityKind.Note]; indexes: { byProject: string } };
  outbox: { key: number; value: OutboxEntry };
  meta: { key: string; value: unknown };
}

/** Object stores holding replicated records, in the order a mixed write locks them. */
const RECORD_STORES = [
  'projects',
  'memberships',
  'people',
  'sessionOverrides',
  'attendanceMarks',
  'notes',
] as const;

/** Every record loaded from disk, grouped by entity family. */
export interface PersistedSnapshot {
  projects: EntityMap[EntityKind.Project][];
  memberships: EntityMap[EntityKind.Membership][];
  people: EntityMap[EntityKind.Person][];
  sessionOverrides: EntityMap[EntityKind.SessionOverride][];
  attendanceMarks: EntityMap[EntityKind.AttendanceMark][];
  notes: EntityMap[EntityKind.Note][];
  outbox: OutboxEntry[];
  cursor: SyncCursor;
  identity: CachedIdentity | null;
}

/**
 * Opens the replica database, creating or extending the object stores as needed.
 *
 * Each version's stores are created in their own branch, so a browser that already holds a
 * replica gains the new store without discarding what it has — including writes still sitting
 * in the outbox.
 */
async function openDatabase(): Promise<IDBPDatabase<Schema>> {
  return openDB<Schema>(DATABASE_NAME, DATABASE_VERSION, {
    upgrade(database, oldVersion) {
      if (oldVersion < 1) {
        database.createObjectStore('projects', { keyPath: 'id' });
        for (const name of ['memberships', 'people', 'sessionOverrides', 'attendanceMarks'] as const) {
          const store = database.createObjectStore(name, { keyPath: 'id' });
          store.createIndex('byProject', 'projectId');
        }
        database.createObjectStore('outbox', { keyPath: 'id', autoIncrement: true });
        database.createObjectStore('meta');
      }
      if (oldVersion < 2) {
        database.createObjectStore('notes', { keyPath: 'id' }).createIndex('byProject', 'projectId');
      }
    },
  });
}

/** Reads `value` as a cached identity, or `null` when it is absent or malformed. */
function decodeIdentity(value: unknown): CachedIdentity | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  if (typeof record.userId !== 'string' || typeof record.email !== 'string') return null;
  return { userId: record.userId, email: record.email };
}

/**
 * The replica's durable storage.
 *
 * All methods are safe to call concurrently; IndexedDB serializes the underlying
 * transactions. Failures propagate so the caller can surface them instead of silently
 * losing a write.
 */
export class Persistence {
  private constructor(private readonly db: IDBPDatabase<Schema>) {}

  /** Opens the database and returns a ready persistence handle. */
  static async open(): Promise<Persistence> {
    return new Persistence(await openDatabase());
  }

  /** Loads the entire replica into memory. */
  async loadAll(): Promise<PersistedSnapshot> {
    const [projects, memberships, people, sessionOverrides, attendanceMarks, notes, outbox, cursor, identity] =
      await Promise.all([
        this.db.getAll('projects'),
        this.db.getAll('memberships'),
        this.db.getAll('people'),
        this.db.getAll('sessionOverrides'),
        this.db.getAll('attendanceMarks'),
        this.db.getAll('notes'),
        this.db.getAll('outbox'),
        this.db.get('meta', CURSOR_KEY),
        this.db.get('meta', IDENTITY_KEY),
      ]);
    return {
      projects,
      memberships,
      people,
      sessionOverrides,
      attendanceMarks,
      notes,
      outbox,
      cursor: typeof cursor === 'number' ? cursor : 0,
      identity: decodeIdentity(identity),
    };
  }

  /**
   * Writes many records of mixed kinds in one transaction.
   *
   * Each kind is dispatched explicitly so the object store's value type is checked rather
   * than asserted; adding an entity kind therefore fails to compile until it is handled.
   */
  async putRecords(ops: readonly PushOp[]): Promise<void> {
    if (ops.length === 0) return;
    const tx = this.db.transaction(RECORD_STORES, 'readwrite');
    const writes = ops.map((op) => {
      switch (op.kind) {
        case EntityKind.Project: return tx.objectStore('projects').put(op.record);
        case EntityKind.Membership: return tx.objectStore('memberships').put(op.record);
        case EntityKind.Person: return tx.objectStore('people').put(op.record);
        case EntityKind.SessionOverride: return tx.objectStore('sessionOverrides').put(op.record);
        case EntityKind.AttendanceMark: return tx.objectStore('attendanceMarks').put(op.record);
        case EntityKind.Note: return tx.objectStore('notes').put(op.record);
      }
    });
    await Promise.all([...writes, tx.done]);
  }

  /** Appends a write to the outbox and returns its delivery order. */
  async enqueue(op: PushOp): Promise<number> {
    return this.db.add('outbox', { op });
  }

  /** Removes delivered writes from the outbox. */
  async dequeue(ids: readonly number[]): Promise<void> {
    if (ids.length === 0) return;
    const tx = this.db.transaction('outbox', 'readwrite');
    await Promise.all([...ids.map((id) => tx.store.delete(id)), tx.done]);
  }

  /** Reads the queued writes in delivery order. */
  async readOutbox(): Promise<OutboxEntry[]> {
    return this.db.getAll('outbox');
  }

  /** Stores the replication cursor reached by the last successful pull. */
  async setCursor(cursor: SyncCursor): Promise<void> {
    await this.db.put('meta', cursor, CURSOR_KEY);
  }

  /** Caches the signed-in identity so the app can start before Clerk loads. */
  async setIdentity(identity: CachedIdentity): Promise<void> {
    await this.db.put('meta', identity, IDENTITY_KEY);
  }

  /**
   * Clears every record, the outbox, and the cursor.
   *
   * Used when a different account signs in, so one user's roster never leaks into another's
   * session on a shared device.
   */
  async clear(): Promise<void> {
    const names = [...RECORD_STORES, 'outbox', 'meta'] as const;
    const tx = this.db.transaction(names, 'readwrite');
    await Promise.all([...names.map((name) => tx.objectStore(name).clear()), tx.done]);
  }
}
